import React from 'react';
import './MunicipalityTable.css'

import { Municipalities, MunicipalityNameValues } from 'lib/Types';

import municipalitiesJson from "data/municipality.json";
import translationsJson from "data/translations.json";
import info from "data/info.json";
import SearchIcon from '@mui/icons-material/Search';
import { InputAdornment, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField } from '@mui/material';
import ExcelExport from 'components/ExcelExport/ExcelExport';

enum SortOrder {
    Ascending = "asc",
    Descending = "desc"
}

interface Props {
    data: MunicipalityNameValues;
    category: string | undefined;
    title: string;
    name: string;
}

interface State {
    sortParameter: number;
    sortOrder: SortOrder;
    page: number;
    pageSize: number;
    searchValue: string;
}

interface DataEntry {
    id: string;
    name: string;
    pop: number;
    count: number;
    percent: number;
}

const municipalities: Municipalities = municipalitiesJson;
const translations: Translations = translationsJson;

class MunicipalityTable extends React.Component<Props, State> {

    constructor(props: Props) {
        super(props);

        this.state = {
            sortParameter: 2,
            sortOrder: SortOrder.Descending,
            page: 0,
            pageSize: 10,
            searchValue: ""
        };
        
		this.onSortClick = this.onSortClick.bind(this);
    }

    private onSortClick(parameter: number) {
        let sortOrder = this.state.sortOrder;
        if (parameter === this.state.sortParameter) {
            sortOrder = sortOrder === SortOrder.Ascending ? SortOrder.Descending : SortOrder.Ascending;
        } else {
            sortOrder = SortOrder.Descending;
        }

        this.setState({ sortParameter: parameter, sortOrder: sortOrder });
    }

    private async onChange(inputStr: string) {
        this.setState({ searchValue: inputStr });
    }

    render() {
        if(Object.keys(this.props.data).length === 0) {
            return (<></>);
        }

        let data: DataEntry[] = [];
        for (let id in this.props.data) {
            if (id !== '0') {
                const munData = municipalities[id];
                const name: string = munData.name[window.lang];
                const count = this.props.data[id];
                const countVal = count === -1 ? 0 : count;
                if (name.toUpperCase().includes(this.state.searchValue.toUpperCase())) {
                    data.push({ id: id, name: name, pop: munData.population, count: count, percent: count === -1 ? ((info.nameCountCutoff / munData.population) * 100 -100) : (countVal / munData.population) * 100 });
                }
            }
        }

        data.sort((a, b) => {
            let valA: string | number;
            let valB: string | number;
            if (this.state.sortParameter === 0) {
                valA = a.name;
                valB = b.name;
            } else if (this.state.sortParameter === 1) {
                valA = a.pop;
                valB = b.pop;
            } else if (this.state.sortParameter === 3) {
                valA = a.percent;
                valB = b.percent;
            } else {
                valA = a.count;
                valB = b.count;
            }

            if (this.state.sortOrder === SortOrder.Ascending) {
                const tmp: string | number = valA;
                valA = valB;
                valB = tmp;
            }

            if (typeof valA === "string" || typeof valB === "string") {
                if (valA < valB)
                    return -1;
                if (valA > valB)
                    return 1;
                return 0;
            } else {
                return valB - valA;
            }
        });



        const csvData = [];

        for(let i = 0; i < data.length; i++) {
            csvData.push([data[i].name, data[i].pop, (data[i].count === -1 ? "<" + info.nameCountCutoff : data[i].count), (data[i].count === -1 ? "<" + (data[i].percent + 100).toLocaleString('it-IT', {useGrouping: true, maximumFractionDigits: 1}) : data[i].percent.toLocaleString('it-IT', {useGrouping: true, maximumFractionDigits: 1}))])
        }

        let tableData = [];
        for(let i = this.state.page * this.state.pageSize; i < data.length && i < (this.state.page + 1) * this.state.pageSize; i++) {
            tableData.push(
                <TableRow hover tabIndex={-1}>
                    <TableCell>
                        {data[i].name}
                    </TableCell>
                    <TableCell>
                        {data[i].pop.toLocaleString('it-IT', {useGrouping: true, maximumFractionDigits: 1})}
                    </TableCell>
                    <TableCell>
                        {data[i].count === -1 ? "<" + info.nameCountCutoff : data[i].count.toLocaleString('it-IT', {useGrouping: true})}
                    </TableCell>
                    <TableCell>
                        {data[i].count === -1 ? "<" + (data[i].percent + 100).toLocaleString('it-IT', {useGrouping: true, maximumFractionDigits: 1}) : data[i].percent.toLocaleString('it-IT', {useGrouping: true, maximumFractionDigits: 1})}
                    </TableCell>
                </TableRow>
            )
        }

        const handleChangePage = (event: unknown, newPage: number) => {
            this.setState({page: newPage});
        };

        const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
            this.setState({pageSize: Number(event.target.value), page: 0});
        };

        
        const createSortHandler = (property: number) => (event: React.MouseEvent<unknown>) => {
            this.onSortClick(property);
        };
        
        const categoryColor = this.props.category === "f" ? "#c5192d" : (this.props.category === "m" ? "#19486a" : "#3f7e44")
        return (
                <Paper className='municipality-table' sx={{ width: '100%', overflow: 'hidden' }}>
                    <div className="table-header">
                        <div className="table-header-title" style={{color: categoryColor}}>
                            {this.props.title}
                        </div>
                        <div className="table-header-operations">
                            <div className="table-header-operations-search">
                                <TextField label={translations.searchMunicipality[window.lang]} placeholder={translations.searchMunicipality[window.lang]} spellCheck={false} 
                                    onChange={e => this.onChange(e.target.value)} value={this.state.searchValue}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                    variant="outlined" type="search" className='searchbar-input-container'
                                />
                            </div>
                            <div className="table-header-operations-export">
                                <ExcelExport data={csvData} category={this.props.category} title={this.props.title} name={this.props.name}></ExcelExport>
                            </div>
                        </div>
                    </div>
                    <TableContainer>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        key="municipality"
                                        align="left"
                                    >
                                        <TableSortLabel
                                            active={this.state.sortParameter === 0}
                                            direction={this.state.sortParameter === 0 ? this.state.sortOrder : 'asc'}
                                            onClick={createSortHandler(0)}
                                        >
                                            {translations.municipality[window.lang]}
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell
                                        key="population"
                                        align="left"
                                    >
                                        <TableSortLabel
                                            active={this.state.sortParameter === 1}
                                            direction={this.state.sortParameter === 1 ? this.state.sortOrder : 'asc'}
                                            onClick={createSortHandler(1)}
                                        >
                                            {translations.population[window.lang]}
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell
                                        key="count"
                                        align="left"
                                    >
                                        <TableSortLabel
                                            active={this.state.sortParameter === 2}
                                            direction={this.state.sortParameter === 2 ? this.state.sortOrder : 'asc'}
                                            onClick={createSortHandler(2)}
                                        >
                                            {translations.count[window.lang]}
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell
                                        key="percent"
                                        align="left"
                                    >
                                        <TableSortLabel
                                            active={this.state.sortParameter === 3}
                                            direction={this.state.sortParameter === 3 ? this.state.sortOrder : 'asc'}
                                            onClick={createSortHandler(3)}
                                        >
                                            {translations.percent[window.lang]}
                                        </TableSortLabel>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tableData}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 100]}
                        component="div"
                        count={data.length}
                        rowsPerPage={this.state.pageSize}
                        page={this.state.page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelDisplayedRows={({ from, to, count }) => {
                                return '' + from + '-' + to + ' ' + translations.of[window.lang] + ' ' + count
                            }
                        }
                        labelRowsPerPage={translations.rowsPerPage[window.lang]}
                    />
            </Paper>
        );
    }
}

export default MunicipalityTable;